// Shrimp performance calculation utilities
// Supports partial harvests, standing biomass, FCR, and survival estimation

/**
 * Partial harvest record type
 */
export interface PartialHarvest {
  count: number; // number of shrimp harvested
  abw: number;   // average body weight at harvest (grams)
}

/**
 * Calculate total partial harvest biomass (kg)
 */
export function calculatePartialHarvestBiomass(partialHarvests: PartialHarvest[]): number {
  return partialHarvests.reduce((sum, h) => sum + (h.count * h.abw) / 1000, 0);
}

/**
 * Calculate current standing biomass (kg)
 * @param stockingNumber Stocked number of shrimp
 * @param survival Survival rate (0-1)
 * @param currentABW Current average body weight (grams)
 */
export function calculateStandingBiomass(stockingNumber: number, survival: number, currentABW: number): number {
  return stockingNumber * survival * currentABW / 1000;
}

/**
 * Calculate total biomass produced (kg)
 */
export function calculateTotalBiomass(partialBiomass: number, standingBiomass: number): number {
  return partialBiomass + standingBiomass;
}

/**
 * Calculate Feed Conversion Ratio (FCR)
 * @param totalFeedUsed Total feed used (kg)
 * @param totalBiomass Total biomass produced (kg)
 */
export function calculateFCR(totalFeedUsed: number, totalBiomass: number): number {
  return totalBiomass > 0 ? totalFeedUsed / totalBiomass : 0;
}

/**
 * Estimate survival rate (0-1) given target FCR using binary search
 * @param stockingNumber Stocked number of shrimp
 * @param totalFeedUsed Total feed used (kg)
 * @param partialHarvests Array of partial harvests
 * @param currentABW Current average body weight (grams)
 * @param targetFCR Target FCR to match
 * @param tolerance Acceptable error in FCR
 * @returns Estimated survival rate (0-1)
 */
export function estimateSurvival(
  stockingNumber: number,
  totalFeedUsed: number,
  partialHarvests: PartialHarvest[],
  currentABW: number,
  targetFCR: number,
  tolerance = 0.001
): number {
  let low = 0.5, high = 1.0, best = 0.8;
  for (let i = 0; i < 30; i++) {
    const mid = (low + high) / 2;
    const partialBiomass = calculatePartialHarvestBiomass(partialHarvests);
    const standingBiomass = calculateStandingBiomass(stockingNumber, mid, currentABW);
    const totalBiomass = calculateTotalBiomass(partialBiomass, standingBiomass);
    const fcr = calculateFCR(totalFeedUsed, totalBiomass);
    if (Math.abs(fcr - targetFCR) < tolerance) {
      return mid;
    }
    if (fcr > targetFCR) {
      high = mid;
    } else {
      low = mid;
    }
    best = mid;
  }
  return best;
} 